import React, { useState, useEffect } from 'react';
import './DesignCritique.css';

// ── Witty one-liners by vibe ─────────────────────────────────
const WITTY_LINES = {
  dark: [
    "Moody enough to make a vampire jealous. 🧛",
    "If shadows could wear clothes, they'd order this.",
    "For when you want to look like a noir film protagonist.",
    "The aesthetic equivalent of 3am existential thoughts.",
  ],
  bright: [
    "So vibrant it needs its own sunscreen. ☀️",
    "Warning: may cause spontaneous happiness.",
    "Your retinas called — they're overwhelmed (in a good way).",
    "If joy was a t-shirt design, it'd look like this.",
  ],
  nature: [
    "Mother Nature texted — she wants her vibe back. 🌿",
    "The trees approve. The squirrels are undecided.",
    "Certified forest-bather aesthetic.",
    "Wear it outside for full immersion. Bugs may follow.",
  ],
  cyber: [
    "The year 2087 called — it wants its aesthetic back. ⚡",
    "Runs on caffeine and neon dreams.",
    "Your CPU just overclocked from the vibes alone.",
    "If the Matrix had a dress code, this would pass.",
  ],
  cosmic: [
    "Somewhere in space, aliens are copying this. 🌌",
    "Big galaxy brain energy. Certified.",
    "The Hubble telescope forwarded this to NASA.",
    "The cosmos approved after a brief committee meeting.",
  ],
  animal: [
    "The animal kingdom sends its regards. 🐾",
    "Your spirit animal just liked this post.",
    "Nature's finest, now available on cotton.",
    "Even the grumpy cat would approve.",
  ],
  food: [
    "Deliciously abstract. Don't lick it. 🍕",
    "Hungry or just inspired? Both valid.",
    "Michelin stars, but for wearable art.",
    "Gordon Ramsay would critique the design, not the food.",
  ],
  default: [
    "The algorithm stared into this and felt something. 🎨",
    "AI had a vision. Humans had a prompt. This happened.",
    "Prompt → Pixels → Pure magic. 🤌",
    "Your creativity levels: dangerously high.",
    "Certified fresh by the creative council.",
    "10/10, would generate again.",
  ],
};

function getWittyLine(prompt) {
  const p = (prompt || '').toLowerCase();
  if (/dark|black|shadow|night|gothic|skull|void|abyss|noir/i.test(p)) return pick(WITTY_LINES.dark);
  if (/sun|bright|rainbow|colorful|vivid|neon|fluorescent|light/i.test(p)) return pick(WITTY_LINES.bright);
  if (/nature|forest|tree|mountain|zen|leaf|flower|botanical|plant/i.test(p)) return pick(WITTY_LINES.nature);
  if (/cyber|punk|robot|tech|digital|circuit|hacker|matrix|glitch/i.test(p)) return pick(WITTY_LINES.cyber);
  if (/galaxy|space|star|cosmos|universe|nebula|astro|cosmic/i.test(p)) return pick(WITTY_LINES.cosmic);
  if (/cat|dog|wolf|dragon|tiger|lion|bear|fox|bunny|animal|creature/i.test(p)) return pick(WITTY_LINES.animal);
  if (/pizza|sushi|taco|coffee|donut|burger|food|fruit|cake/i.test(p)) return pick(WITTY_LINES.food);
  return pick(WITTY_LINES.default);
}

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// ── Scoring logic ───────────────────────────────────────────
function computePromptScores(prompt) {
  const p = (prompt || '').toLowerCase();
  const words = p.trim().split(/\s+/).filter(Boolean);
  const wordCount = words.length;
  const uniqueRatio = new Set(words).size / Math.max(wordCount, 1);

  // Originality: more words, more unique words, rare/evocative keywords boost score
  const evocativeWords = ['surreal','abstract','cosmic','ethereal','mystical','enchanted',
    'ancient','futuristic','dreamlike','holographic','prismatic','legendary','divine',
    'psychedelic','mythological','transcendent','otherworldly','luminescent','cinematic','epic'];
  const evocativeHits = words.filter(w => evocativeWords.some(ev => w.includes(ev))).length;

  const originality = Math.min(5, Math.max(1, Math.round(
    1 +
    (wordCount >= 4 ? 1 : 0) +
    (wordCount >= 8 ? 1 : 0) +
    (uniqueRatio > 0.85 ? 1 : 0) +
    Math.min(1, evocativeHits)
  )));

  // Print Suitability: bold/high-contrast styles are better for print
  const printFriendly = ['bold','stark','geometric','graphic','simple','minimal','flat','solid','black and white','monochrome'];
  const printUnfriendly = ['pastel','soft','watercolor','gradient','subtle','muted','delicate','photorealistic'];
  const hasFriendly = printFriendly.some(kw => p.includes(kw));
  const hasUnfriendly = printUnfriendly.some(kw => p.includes(kw));
  let printSuitability = 3;
  if (hasFriendly) printSuitability = Math.min(5, printSuitability + 1.5);
  if (hasUnfriendly) printSuitability = Math.max(1, printSuitability - 1);
  printSuitability = Math.round(Math.min(5, Math.max(1, printSuitability)));

  return { originality, printSuitability };
}

function analyzeColorBalance(imageUrl, prompt) {
  return new Promise((resolve) => {
    const fallback = () => {
      // Deterministic fallback from prompt seed
      const seed = ((prompt || '').length * 7 + 13) % 3 + 2; // 2–4
      resolve(seed);
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const SIZE = 60;
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        const data = ctx.getImageData(0, 0, SIZE, SIZE).data;

        let rSum = 0, gSum = 0, bSum = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
          rSum += data[i]; gSum += data[i + 1]; bSum += data[i + 2];
          count++;
        }
        const rAvg = rSum / count, gAvg = gSum / count, bAvg = bSum / count;
        const mean = (rAvg + gAvg + bAvg) / 3;
        const variance = (
          (rAvg - mean) ** 2 + (gAvg - mean) ** 2 + (bAvg - mean) ** 2
        ) / 3;

        // Low variance → balanced channels → high score
        const balance = Math.round(1 + (1 - Math.min(variance / 4500, 1)) * 4);
        resolve(Math.min(5, Math.max(1, balance)));
      } catch {
        fallback();
      }
    };
    img.onerror = fallback;
    img.src = imageUrl;
  });
}

// ── Star rating sub-component ───────────────────────────────
function StarRating({ value, label, color }) {
  return (
    <div className="critique-rating-row">
      <span className="critique-rating-label">{label}</span>
      <div className="critique-stars">
        {[1, 2, 3, 4, 5].map(star => (
          <span
            key={star}
            className={`critique-star${star <= value ? ' filled' : ''}`}
            style={star <= value ? { color } : undefined}
          >★</span>
        ))}
      </div>
      <span className="critique-score">{value}/5</span>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────
export default function DesignCritique({ imageUrl, prompt }) {
  const [open, setOpen] = useState(false);
  const [colorBalance, setColorBalance] = useState(null);
  const [wittyLine] = useState(() => getWittyLine(prompt));

  useEffect(() => {
    if (!imageUrl) { setColorBalance(null); return; }
    analyzeColorBalance(imageUrl, prompt).then(setColorBalance);
  }, [imageUrl, prompt]);

  const { originality, printSuitability } = computePromptScores(prompt);
  const colorScore = colorBalance ?? 3;
  const overall = Math.round((colorScore + originality + printSuitability) / 3);

  const overallMeta = [
    { label: 'Needs Work 😬', bg: '#dc2626' },
    { label: 'Decent 👍', bg: '#f59e0b' },
    { label: 'Good ✨', bg: '#0ea5e9' },
    { label: 'Great 🔥', bg: '#7c3aed' },
    { label: 'Masterpiece! 🎨', bg: '#059669' },
  ];
  const meta = overallMeta[Math.min(overall - 1, 4)] || overallMeta[2];

  return (
    <div className={`design-critique${open ? ' dc-open' : ''}`}>
      <button className="critique-toggle" onClick={() => setOpen(o => !o)}>
        <span className="critique-icon">🎭</span>
        <span className="critique-label">AI Design Critique</span>
        <span className="critique-badge" style={{ background: meta.bg }}>{meta.label}</span>
        <span className={`critique-chevron${open ? ' up' : ''}`}>▾</span>
      </button>

      {open && (
        <div className="critique-body fade-in">
          <div className="critique-ratings">
            <StarRating value={colorScore} label="🎨 Color Balance" color="#f472b6" />
            <StarRating value={originality} label="💡 Originality" color="#a78bfa" />
            <StarRating value={printSuitability} label="🖨️ Print Suitability" color="#34d399" />
          </div>
          <div className="critique-divider" />
          <div className="critique-oneliner">
            <span className="cq">"</span>
            {wittyLine}
            <span className="cq">"</span>
          </div>
        </div>
      )}
    </div>
  );
}
